<template>
  <b-card>
    <b-card-title class="mb-1">
      <feather-icon
        icon="ActivityIcon"
        size="24"
        class="mr-1"
      />
      Aktivitäten-Timeline
    </b-card-title>

    <b-card-body class="p-0">
      <!-- Loading State -->
      <div
        v-if="loading"
        class="d-flex justify-content-center align-items-center"
        style="min-height: 200px;"
      >
        <b-spinner class="align-middle mr-2" />
        <small>Lade Aktivitäten...</small>
      </div>

      <!-- Timeline Content -->
      <div
        v-else
        class="timeline-container"
      >
        <div class="timeline">
          <!-- Recent Activity Items -->
          <div
            v-for="(activity, index) in activities"
            :key="index"
            class="timeline-item"
          >
            <div class="timeline-point">
              <feather-icon
                :icon="activity.icon"
                :class="`text-${activity.variant}`"
                size="14"
              />
            </div>
            <div class="timeline-content">
              <div class="d-flex justify-content-between align-items-start mb-1">
                <h6 class="mb-0 text-body">
                  {{ activity.title }}
                </h6>
                <small class="text-muted">
                  {{ getRelativeTime(activity.timestamp) }}
                </small>
              </div>
              <p class="mb-0 text-muted">
                {{ activity.description }}
              </p>
              <div
                v-if="activity.metadata"
                class="mt-1"
              >
                <b-badge
                  v-for="(value, key) in activity.metadata"
                  :key="key"
                  variant="light-secondary"
                  class="mr-1"
                >
                  {{ key }}: {{ value }}
                </b-badge>
              </div>
            </div>
          </div>

          <!-- No activities message -->
          <div
            v-if="activities.length === 0"
            class="text-center py-3"
          >
            <feather-icon
              icon="ClockIcon"
              size="48"
              class="text-muted mb-2"
            />
            <p class="text-muted mb-0">
              Keine kürzlichen Aktivitäten gefunden
            </p>
          </div>
        </div>
      </div>

    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardBody,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import helper from '@/helper/helper'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardBody,
    BBadge,
    BSpinner,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { getFormattedDate } = helper()
    return { getFormattedDate }
  },
  data() {
    return {
      loading: false,
      activities: [],
    }
  },
  mounted() {
    this.loadActivities()
  },
  methods: {
    async loadActivities() {
      this.loading = true
      try {
        // Simulate API call - replace with actual implementation
        await new Promise(resolve => setTimeout(resolve, 1000))

        // Generate mock activity data based on user data
        this.activities = this.generateMockActivities()
      } catch (error) {
        console.error('Error loading activities:', error)
      } finally {
        this.loading = false
      }
    },
    generateMockActivities() {
      const now = new Date()
      const activities = []

      // Account creation
      if (this.user.created_at) {
        activities.push({
          title: 'Konto erstellt',
          description: 'Benutzerkonto wurde erfolgreich erstellt',
          timestamp: this.user.created_at,
          icon: 'UserPlusIcon',
          variant: 'success',
          metadata: {
            Status: 'Erfolgreich'
          }
        })
      }

      // Last update
      if (this.user.updated_at && this.user.updated_at !== this.user.created_at) {
        activities.push({
          title: 'Profil aktualisiert',
          description: 'Benutzerprofil wurde zuletzt bearbeitet',
          timestamp: this.user.updated_at,
          icon: 'EditIcon',
          variant: 'info',
          metadata: {
            Typ: 'Profil-Update'
          }
        })
      }

      // Email confirmation
      if (this.user.confirmed) {
        activities.push({
          title: 'E-Mail bestätigt',
          description: 'E-Mail-Adresse wurde erfolgreich verifiziert',
          timestamp: this.user.created_at, // Approximate - would need actual confirmation date
          icon: 'MailIcon',
          variant: 'success',
          metadata: {
            Email: this.user.email
          }
        })
      }

      // Role assignment
      if (this.user.role) {
        activities.push({
          title: 'Rolle zugewiesen',
          description: `Rolle "${this.user.role.name}" wurde zugewiesen`,
          timestamp: this.user.created_at, // Approximate
          icon: 'ShieldIcon',
          variant: 'primary',
          metadata: {
            Rolle: this.user.role.name
          }
        })
      }

      // If blocked
      if (this.user.blocked) {
        activities.push({
          title: 'Konto gesperrt',
          description: 'Benutzerkonto wurde gesperrt',
          timestamp: new Date(now.getTime() - Math.random() * 7 * 24 * 60 * 60 * 1000), // Random within last week
          icon: 'LockIcon',
          variant: 'danger',
          metadata: {
            Status: 'Gesperrt'
          }
        })
      }

      // Sort by timestamp (newest first)
      return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    },
    getRelativeTime(timestamp) {
      const now = new Date()
      const time = new Date(timestamp)
      const diffInSeconds = Math.floor((now - time) / 1000)

      if (diffInSeconds < 60) return 'Gerade eben'
      if (diffInSeconds < 3600) return `vor ${Math.floor(diffInSeconds / 60)} Min`
      if (diffInSeconds < 86400) return `vor ${Math.floor(diffInSeconds / 3600)} Std`
      if (diffInSeconds < 2592000) return `vor ${Math.floor(diffInSeconds / 86400)} Tagen`
      if (diffInSeconds < 31536000) return `vor ${Math.floor(diffInSeconds / 2592000)} Monaten`

      return `vor ${Math.floor(diffInSeconds / 31536000)} Jahren`
    },
  },
}
</script>

<style lang="scss" scoped>
.timeline-container {
  max-height: 400px;
  overflow-y: auto;
  padding: 1rem;
}

.timeline {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 10px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #ebe9f1;
  }
}

.timeline-item {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.timeline-point {
  position: absolute;
  left: 0;
  top: 2px;
  width: 20px;
  height: 20px;
  background: white;
  border: 2px solid #ebe9f1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.timeline-content {
  background: #f8f8f8;
  border-radius: 0.375rem;
  padding: 0.75rem;
  border-left: 3px solid #7367f0;
}

// Custom scrollbar
.timeline-container::-webkit-scrollbar {
  width: 4px;
}

.timeline-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.timeline-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.timeline-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

</style>
